import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/DataReviewPage.css';
import PaginationNav from '../components/PaginationNav';
import ReviewDataItem from '../components/ReviewDataItem';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import { useNotifications } from '../contexts/NotificationsContext';
import GroupSelector from '../components/GroupSelector';

function DataReviewPage() {
  const { appendGroupToUrl, currentGroup, buildGroupApiUrl, activeSuffix, groups } = useGroup();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  // Initialize page size from cookie, fallback to 100, and validate
  const getCookie = (name) => {
    try {
      const escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };
  const setCookie = (name, value, days = 365) => {
    try {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/`;
    } catch {}
  };
  const allowedPageSizes = [10, 20, 50, 100];
  const initialPageSize = (() => {
    const fromCookie = parseInt(getCookie('dataReviewPageSize') || '', 10);
    if (allowedPageSizes.includes(fromCookie)) return fromCookie;
    // Align default with DataManagementPage for consistent pipeline UX
    return 50;
  })();
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState(null);
  const [executingPromptId, setExecutingPromptId] = useState(null);
  const [promptButtons, setPromptButtons] = useState([]);
  const promptsLoadedRef = useRef(false);
  const promptsLoadingRef = useRef(false);
  // Track last group used for loading prompts so we can reload when the group changes
  const lastGroupForPromptsRef = useRef(null);
  // Map of entry_id -> full approved entry (from data management API)
  const [entriesById, setEntriesById] = useState({});
  // Unified keys from group to align visible columns with Data Management page
  const [unifiedRawKeys, setUnifiedRawKeys] = useState([]);
  const [unifiedOutputKeys, setUnifiedOutputKeys] = useState([]);
  // Stats for header/widgets: total items, processed, pending (setter used to update, value not currently rendered)
  const [, setStats] = useState({ total: 0, processed: 0, pending: 0 });
  // Review sources with pending counts (for header summary) — from shared context
  const { reviewSources } = useNotifications();
  // De-dup and cancellation for main list loading
  const listAbortRef = useRef(null);
  const listInFlightKeyRef = useRef(null);
  // Optional: fetch full entry details for rows.
  // Keep disabled by default to avoid an extra `/entries/query` call during initial load.
  const enableEntryDetails = true;
  // Actions abstraction per row id
  const actionsByIdRef = useRef(new Map());

  // NOTE: actionsByIdRef is rebuilt after action handlers are defined to avoid TDZ errors

  // Helper: extract a readable error string from various server response shapes
  const extractErrorText = (val, fallback = 'An error occurred') => {
    if (!val) return fallback;
    if (typeof val === 'string') {
      // If the string looks like JSON, try to extract a .message or .error field
      const trimmed = val.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          return parsed.message || parsed.error || parsed.detail || fallback;
        } catch (_) {
          return fallback;
        }
      }
      return val;
    }
    if (typeof val === 'object') {
      return val.message || val.error || val.detail || fallback;
    }
    return String(val) || fallback;
  };

  // Load review rows using the new query API that returns count + totalCount
  const loadRows = async (showLoading = true) => {
    console.log('[DataReviewPage] loadRows called, showAll:', showAll, 'page:', page, 'pageSize:', pageSize);
    
    // If there's no current group, don't attempt to load rows
    if (!currentGroup) {
      console.log('[DataReviewPage] No current group selected, skipping load');
      setRows([]);
      setTotal(0);
      setStats({ total: 0, processed: 0, pending: 0 });
      if (showLoading) setLoading(false);
      return;
    }
    
    if (showLoading) {
      setLoading(true);
      setMessage(null); // Clear any previous error when starting a new user-triggered load
    }
    try {
      // Use POST /entries/query with filters + pagination
      const offset = (Math.max(1, page) - 1) * pageSize;
      // Build a stable key for the intended request. Include group id/slug if available.
      const groupKey = currentGroup && (currentGroup.slug || currentGroup.groupSlug || currentGroup.id || '');
      const reqKey = `${groupKey}|${page}|${pageSize}|${showAll ? 'all' : 'pending'}`;

      // If the exact same request is already in flight and not aborted, skip sending another to save bandwidth
      if (
        listInFlightKeyRef.current === reqKey &&
        listAbortRef.current &&
        !(listAbortRef.current.signal && listAbortRef.current.signal.aborted)
      ) {
        if (showLoading) setLoading(false);
        return;
      }

      // Abort any previous in-flight request (different key)
      if (listAbortRef.current) {
        try { listAbortRef.current.abort(); } catch {}
      }
      const controller = new AbortController();
      listAbortRef.current = controller;
      listInFlightKeyRef.current = reqKey;
      const body = {
        filters: showAll ? {} : { reviewed: false },
        limit: pageSize,
        offset,
        orderBy: 'created_at',
        orderDirection: 'desc',
      };
      const headers = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl('/entries/query'), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (response.ok) {
        const data = await response.json();
        // Normalize list payload across possible shapes: data, items, entries
        const newRows = Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data?.items)
            ? data.items
            : (Array.isArray(data?.entries) ? data.entries : []));

        // Mirror DataManagementPage: if backend signals explicit failure, show error
        if (data && data.success === false) {
          if (showLoading) {
            setMessage({ type: 'error', title: 'Error:', text: extractErrorText(data.error, 'Failed to load rows') });
          }
          return;
        }

        // Accept rows even if the backend did not set `success` explicitly
        if (Array.isArray(newRows)) {
          setRows(prevRows => {
            if (JSON.stringify(prevRows) === JSON.stringify(newRows)) {
              return prevRows;
            }
            return newRows;
          });
          // New API returns `count` (items in current page) and `totalCount` (all matching items)
          const pageCount = typeof data.count === 'number' ? data.count : newRows.length;
          const totalCount = typeof data.totalCount === 'number'
            ? data.totalCount
            : (typeof data.pagination?.totalEntries === 'number' ? data.pagination.totalEntries : pageCount);
          setTotal(Number(totalCount));
          // Keep existing stats structure for header widgets if used elsewhere
          const pendingCount = newRows.filter(r => r && r.reviewed === false).length;
          const processedCount = newRows.filter(r => r && r.reviewed === true).length;
          setStats({ total: Number(totalCount), processed: processedCount, pending: pendingCount });
        } else {
          if (showLoading) {
            setMessage({
              type: 'error',
              title: 'Error:',
              text: extractErrorText(data?.error, 'Failed to load rows')
            });
          }
        }
      } else {
        if (showLoading) {
          const errorData = await response.json().catch(() => ({}));
          setMessage({
            type: 'error',
            title: 'Error:',
            text: extractErrorText(errorData.error, 'Failed to load rows')
          });
        }
      }
    } catch (error) {
      // Ignore abort errors as they are expected during fast changes or de-dup
      if (error && (error.name === 'AbortError' || error.code === 20)) {
        // Swallow silently
      } else {
        console.error('[DataReviewPage] Error loading rows:', error);
        if (showLoading) {
          setMessage({
            type: 'error',
            title: 'Error:',
            text: error.message || 'Failed to load rows'
          });
        }
      }
    } finally {
      // Clear in-flight key when this request finishes (only if it is the latest)
      const groupKey = currentGroup && (currentGroup.slug || currentGroup.groupSlug || currentGroup.id || '');
      const latestKey = `${groupKey}|${page}|${pageSize}|${showAll ? 'all' : 'pending'}`;
      if (listInFlightKeyRef.current === latestKey) {
        listInFlightKeyRef.current = null;
      }
      if (showLoading) setLoading(false);
    }
  };

  // Ensure rows load on mount and whenever paging/group/suffix changes
  // Note: buildGroupApiUrl is excluded from deps to avoid reload loops when groups array updates
  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, showAll, currentGroup, activeSuffix]);

  // Fire a guaranteed first-load on initial mount to avoid any edge cases
  // where dependencies might prevent the first effect from running promptly.
  const didInitialLoadRef = useRef(false);
  useEffect(() => {
    if (!didInitialLoadRef.current) {
      didInitialLoadRef.current = true;
      loadRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any in-flight list request on unmount
  useEffect(() => {
    return () => {
      if (listAbortRef.current) {
        try { listAbortRef.current.abort(); } catch {}
        // Ensure we clear the in-flight key so a subsequent mount can reload
        try { listInFlightKeyRef.current = null; } catch {}
      }
    };
  }, []);


  // Server already filters by statuses; no further client-side filtering needed
  const filteredRows = rows;

  useEffect(() => {
    // Reload prompt buttons when ingestion group changes, but avoid duplicate overlapping calls
    if ((promptsLoadedRef.current && !promptsLoadingRef.current) && lastGroupForPromptsRef.current === (currentGroup || '')) {
      return;
    }
    promptsLoadedRef.current = true;
    promptsLoadingRef.current = true;
    lastGroupForPromptsRef.current = (currentGroup || '');

    // Load prompt buttons for review page and filter by current ingestion group
    const loadPromptButtons = async () => {
      try {
        console.log('[DataReviewPage] Loading prompt buttons for group:', currentGroup || '(none)');
        const headers = await getAuthHeaders();
        const response = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/prompts`), {
          method: 'POST',
          headers,
          body: JSON.stringify({ page_location: 'review' }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.prompts)) {
            const group = currentGroup || '';
            const filtered = data.prompts
              .filter(p => (p.ingestion_group_slug || '') === group)
              .filter(p => (p.page_location === 'review'));
            setPromptButtons(filtered);
            console.log('[DataReviewPage] Loaded', filtered.length, 'prompt buttons (filtered by group)');
          } else {
            setPromptButtons([]);
          }
        } else {
          setPromptButtons([]);
        }
      } catch (error) {
        console.error('[DataReviewPage] Error loading prompt buttons:', error);
        promptsLoadedRef.current = false; // Reset on error so it can retry
        setPromptButtons([]);
      } finally {
        promptsLoadingRef.current = false;
      }
    };

    loadPromptButtons();
  }, [appendGroupToUrl, currentGroup]);

  const handleAccept = useCallback(async (rowId) => {
    try {
      // Update entry: reviewed true, approved true
      console.log('[DataReviewPage] Approving entry:', rowId);
      const headers = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl(`/entries/${encodeURIComponent(rowId)}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reviewed: true, approved: true }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[DataReviewPage] Approve response:', data);
        if (data.success) {
          // Update local state optimistically using returned review
          const updated = data.data || data.review || {};
          console.log('[DataReviewPage] Updating local state with:', { id: rowId, reviewed: updated.reviewed, approved: updated.approved });
          setRows(prevRows => prevRows.map(r => (r.id === rowId ? { ...r, ...updated } : r)));
          window.dispatchEvent(new CustomEvent('dataReviewUpdate'));
          // Do not refresh the list; keep the item visible so the user can undo/change the review
        } else {
          console.error('[DataReviewPage] Approve failed:', data.error);
          setMessage({
            type: 'error',
            title: 'Error:',
            text: data.error || 'Failed to accept row'
          });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[DataReviewPage] Approve HTTP error:', response.status, errorData);
        setMessage({
          type: 'error',
          title: 'Error:',
          text: errorData.error || 'Failed to accept row'
        });
      }
    } catch (error) {
      console.error('[DataReviewPage] Error accepting row:', error);
      setMessage({
        type: 'error',
        title: 'Error:',
        text: error.message || 'Failed to accept row'
      });
    }
  }, [buildGroupApiUrl]);

  const handleDeny = useCallback(async (rowId) => {
    try {
      // Update entry: reviewed true, approved false
      const headers = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl(`/entries/${encodeURIComponent(rowId)}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reviewed: true, approved: false }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const updated = data.data || data.review || {};
          setRows(prevRows => prevRows.map(r => (r.id === rowId ? { ...r, ...updated } : r)));
          window.dispatchEvent(new CustomEvent('dataReviewUpdate'));
          // Do not refresh the list; keep the item visible so the user can undo/change the review
        } else {
          setMessage({
            type: 'error',
            title: 'Error:',
            text: data.error || 'Failed to deny row'
          });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({
          type: 'error',
          title: 'Error:',
          text: extractErrorText(errorData.error, 'Failed to deny row')
        });
      }
    } catch (error) {
      console.error('[DataReviewPage] Error denying row:', error);
      setMessage({
        type: 'error',
        title: 'Error:',
        text: error.message || 'Failed to deny row'
      });
    }
  }, [buildGroupApiUrl]);

  const handleMarkForReview = useCallback(async (rowId) => {
    try {
      // Mark unreviewed: reviewed false; keep approved unchanged (server may default approved false)
      const headers = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl(`/entries/${encodeURIComponent(rowId)}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reviewed: false })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        const updated = data.data || data.review || {};
        setRows(prevRows => prevRows.map(r => (r.id === rowId ? { ...r, ...updated } : r)));
        // Do not refresh automatically; keep current list stable for undo/change
      } else {
        setMessage({ type: 'error', title: 'Error:', text: (data && data.error) || 'Failed to mark for review' });
      }
    } catch (e) {
      setMessage({ type: 'error', title: 'Network error:', text: e.message || 'Failed to mark for review' });
    }
  }, [buildGroupApiUrl]);

  const handleDelete = useCallback(async (rowId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl(`/entries/${encodeURIComponent(rowId)}`), { method: 'DELETE', headers });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setRows(prevRows => prevRows.filter(r => r.id !== rowId));
        setTotal(t => Math.max(0, (t || 0) - (data.deleted ? 1 : 0)));
      } else {
        setMessage({ type: 'error', title: 'Error:', text: (data && data.error) || 'Failed to delete review' });
      }
    } catch (e) {
      setMessage({ type: 'error', title: 'Network error:', text: e.message || 'Failed to delete review' });
    }
  }, [buildGroupApiUrl]);

  // Rebuild actions map whenever rows change so actions are decoupled
  useEffect(() => {
    const map = new Map();
    for (const r of rows) {
      if (!r || r.id == null) continue;
      const id = r.id;
      map.set(id, {
        approve: () => handleAccept(id),
        deny: () => handleDeny(id),
        markUnreviewed: () => handleMarkForReview(id),
        delete: () => handleDelete(id),
      });
    }
    actionsByIdRef.current = map;
  }, [rows, handleAccept, handleDeny, handleMarkForReview, handleDelete]);

  // Removed unused helper getFieldNames to satisfy ESLint (no-unused-vars)

  // Load unified keys for the current group (reuse DM page behavior) — guard against double-invoke on mount
  const keysLoadedKeyRef = useRef(null);
  useEffect(() => {
    const loadUnifiedKeys = async () => {
      try {
        if (!currentGroup) return;
        const loadKey = `${activeSuffix}::${currentGroup}`;
        if (keysLoadedKeyRef.current === loadKey) {
          return; // already loaded for this group/suffix
        }
        const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(activeSuffix)}/groups/${encodeURIComponent(currentGroup)}/keys`;
        const keysHeaders = await getAuthHeaders();
        const resp = await fetch(url, { method: 'GET', headers: keysHeaders });
        if (!resp.ok) return;
        const json = await resp.json().catch(() => ({}));
        const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');
        const rawKeys = Array.isArray(json?.data?.raw) ? json.data.raw : [];
        const outExisting = Array.isArray(json?.data?.output?.existing) ? json.data.output.existing : [];
        const outDefined = Array.isArray(json?.data?.output?.defined) ? json.data.output.defined : [];
        const outSet = Array.from(new Set([...(outExisting || []), ...(outDefined || [])].filter((k) => k && isVisibleKey(k))));
        setUnifiedRawKeys(Array.from(new Set([...(rawKeys || [])].filter((k) => k && isVisibleKey(k)))));
        setUnifiedOutputKeys(outSet);
        keysLoadedKeyRef.current = loadKey;
      } catch (_) {
        // ignore; leave empty
      }
    };
    loadUnifiedKeys();
  }, [activeSuffix, currentGroup]);

  // Fetch actual entry data for the current page's rows (approved & reviewed only)
  useEffect(() => {
    if (!enableEntryDetails) {
      setEntriesById({});
      return;
    }
    const fetchEntries = async () => {
      try {
        // Collect unique IDs from rows; also consider alternative field names
        const ids = Array.from(new Set((rows || [])
          .map(r => (r && (r.entry_id || r.entryId || r.id || (r.entry && r.entry.id))))
          .filter(Boolean)));
        if (!ids.length) {
          setEntriesById({});
          return;
        }

        const limited = ids.slice(0, 100);

        // Helper: perform a query with a specific filter key
        const queryWithFilter = async (key) => {
          const body = { filters: { [key]: limited }, limit: Math.max(1, limited.length), offset: 0 };
          const headers = await getAuthHeaders();
          const resp = await fetch(buildGroupApiUrl('/entries/query'), {
            method: 'POST',
            headers,
            body: JSON.stringify(body)
          });
          if (!resp.ok) return [];
          const data = await resp.json().catch(() => ({}));
          const list = Array.isArray(data?.data)
            ? data.data
            : (Array.isArray(data?.items)
              ? data.items
              : (Array.isArray(data?.entries) ? data.entries : []));
          return Array.isArray(list) ? list : [];
        };

        // Try common variants supported by different backends
        let entries = await queryWithFilter('ids');
        if (!entries.length) entries = await queryWithFilter('id__in');
        if (!entries.length) entries = await queryWithFilter('id_in');

        if (entries.length) {
          const map = {};
          entries.forEach((e) => {
            const key = e && (e.id || e.entry_id || e.entryId);
            if (key) map[key] = e;
          });
          setEntriesById(map);
        } else {
          // As a last resort, build a map from any embedded entries on rows
          const fallbackMap = {};
          (rows || []).forEach((r) => {
            const embedded = r && (r.entry || r.data);
            const key = embedded && (embedded.id || embedded.entry_id || embedded.entryId || r.entry_id);
            if (key && embedded) fallbackMap[key] = embedded;
          });
          setEntriesById(fallbackMap);
        }
      } catch (e) {
        // Silently ignore, use fallback
      }
    };
    fetchEntries();
  }, [rows, buildGroupApiUrl, enableEntryDetails]);

  return (
    <div className="data-review-page">
      {message && (
        <div className="result-container" style={{ marginBottom: '20px' }}>
          <div className={`alert alert-${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : message.type === 'error' ? 'exclamation-circle' : 'info-circle'}`}></i>
            <div>
              {message.title && <strong>{message.title}</strong>}
              {message.title && ' '}
              {message.text}
            </div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header">
          <div className="header-content" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ marginRight: 'auto' }}><i className="fas fa-check-circle"></i> Data Review</h2>
            <GroupSelector compact />
            <button
              className={`btn btn-sm ${showAll ? 'btn-secondary' : 'btn-primary'}`}
              onClick={() => { setShowAll(!showAll); setPage(1); }}
            >
              {showAll ? 'Show Pending' : 'Show All'}
            </button>
          </div>
        </div>
        <p className="section-description">
          Review and approve data entries. Only approved entries will be visible in the main interface.
        </p>

        {/* Review Sources Summary - showing groups with unapproved items */}
        {reviewSources.length > 0 && (
          <div className="review-sources-summary">
            {reviewSources.map((source) => (
              <div key={source.id} className="review-source-item">
                <span className="review-source-name">{source.name}</span>
                <span className="review-source-count"> has {source.pendingCount} unapproved item{source.pendingCount !== 1 ? 's' : ''}.</span>
              </div>
            ))}
          </div>
        )}

        {/* Agent Workflows (Prompt Buttons) — moved above the paged review list */}
        {promptButtons.length > 0 && (
          <div className="prompt-buttons-section">
            <h3><i className="fas fa-magic"></i> Agent Workflows</h3>
            <div className="prompt-buttons">
              {promptButtons.map((prompt) => (
                <button
                  key={prompt.id}
                  className="btn btn-secondary prompt-button"
                  disabled={executingPromptId === prompt.id}
                  onClick={async () => {
                    try {
                      setExecutingPromptId(prompt.id);
                      setMessage(null);
                      const headers = await getAuthHeaders();
                      const resp = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/prompt-executions`), {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ prompt_id: prompt.id })
                      });
                      if (resp.ok) {
                        const data = await resp.json();
                        if (data && data.id) {
                          // Notify queue to refresh immediately
                          try {
                            window.dispatchEvent(new CustomEvent('executionRequestCreated', { detail: { id: data.id, prompt_id: prompt.id } }));
                          } catch {}
                          setMessage({
                            type: 'success',
                            title: 'Request created',
                            text: `A new execution request was created for "${prompt.name}". Track progress on Horizon Main under Prompt executions.`,
                          });
                        } else {
                          setMessage({ type: 'error', title: 'Unexpected response:', text: 'The server did not return a request id.' });
                        }
                      } else if (resp.status === 400) {
                        const err = await resp.json().catch(() => ({}));
                        setMessage({ type: 'error', title: 'Invalid request:', text: err.error || 'Invalid prompt id.' });
                      } else if (resp.status === 401) {
                        setMessage({ type: 'error', title: 'Unauthorized:', text: 'You must be signed in to execute prompts.' });
                      } else if (resp.status === 503) {
                        setMessage({ type: 'error', title: 'Service unavailable:', text: 'Please try again later.' });
                      } else {
                        const err = await resp.json().catch(() => ({}));
                        setMessage({ type: 'error', title: 'Error:', text: err.error || `Request failed with status ${resp.status}.` });
                      }
                    } catch (e) {
                      setMessage({ type: 'error', title: 'Network error:', text: e.message || 'Failed to create execution request.' });
                    } finally {
                      setExecutingPromptId(null);
                    }
                  }}
                >
                  {executingPromptId === prompt.id ? 'Creating...' : prompt.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Top Pagination Controls */}
        <PaginationNav
          position="top"
          page={page}
          pageSize={pageSize}
          total={total}
          allowedPageSizes={allowedPageSizes}
          disabled={loading}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(sz) => {
            const next = allowedPageSizes.includes(sz) ? sz : 100;
            setPageSize(next);
            setCookie('dataReviewPageSize', String(next));
          }}
        />

        {/* Rows Container */}
        <div className="rows-container">
          {loading ? (
            <p className="no-results">Loading rows...</p>
          ) : !currentGroup || !groups || groups.length === 0 ? (
            <div className="no-results">
              <p><i className="fas fa-info-circle"></i> No ingestion groups available for this horizon.</p>
              <p style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
                To get started, go to <strong>Settings → Ingestion</strong> to create an ingestion group and associate it with this horizon.
              </p>
            </div>
          ) : filteredRows.length === 0 ? (
            <p className="no-results">
              {showAll
                ? 'No reviewed rows found.'
                : 'No pending rows to review. All rows have been reviewed.'}
            </p>
          ) : (
            filteredRows.map((row) => {
              const actions = actionsByIdRef.current.get(row.id) || {
                approve: () => handleAccept(row.id),
                deny: () => handleDeny(row.id),
                markUnreviewed: () => handleMarkForReview(row.id),
                delete: () => handleDelete(row.id),
              };
              return (
                <ReviewDataItem
                  key={row.id}
                  row={row}
                  entriesById={entriesById}
                  unifiedRawKeys={unifiedRawKeys}
                  unifiedOutputKeys={unifiedOutputKeys}
                  actions={actions}
                  fileDownloadContext={
                    activeSuffix && currentGroup
                      ? { suffix: activeSuffix, groupSlug: currentGroup }
                      : null
                  }
                />
              );
            })
          )}
        </div>

        {/* Bottom Pagination Controls */}
        <PaginationNav
          position="bottom"
          page={page}
          pageSize={pageSize}
          total={total}
          allowedPageSizes={allowedPageSizes}
          disabled={loading}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(sz) => {
            const next = allowedPageSizes.includes(sz) ? sz : 100;
            setPageSize(next);
            setCookie('dataReviewPageSize', String(next));
          }}
        />
      </div>
    </div>
  );
}

export default DataReviewPage;
